/**
 * The folder trail behind the SharePoint tab's breadcrumbs.
 *
 * The URL addresses ONE folder (`sp_folder`), which is all a link needs — the pair `drive_id` +
 * `item_id` locates it on its own. The chain of ancestors, and every folder's display name, only exist
 * while the user is drilling in, so they live in component state and are reconciled against the URL here.
 */

/** One folder on the way down. `name` is null for a folder nobody in this session walked into. */
export interface FolderTrailCrumb {
  itemId: string;
  name: string | null;
}

/**
 * The trail to render for the folder the URL points at, given the chain the session remembers.
 *
 * Three cases, and the second is why this is not just "use the remembered chain": going back (the
 * browser's Back button, or a click on a breadcrumb) moves the URL to a folder in the middle of the
 * chain, and the trail has to cut there. A folder the session has never seen — a pasted link — has no
 * chain and no name, so it stands alone and the breadcrumb labels it generically rather than showing a
 * raw Graph id.
 */
export function resolveFolderTrail(
  remembered: readonly FolderTrailCrumb[],
  folderId: string | null,
): FolderTrailCrumb[] {
  if (folderId === null || folderId === 'root') return [];
  const depth = remembered.findIndex((crumb) => crumb.itemId === folderId);
  if (depth >= 0) return remembered.slice(0, depth + 1);
  return [{ itemId: folderId, name: null }];
}
